import { createListenerMiddleware } from '@reduxjs/toolkit';
import { locationChanged } from '../ducks/routing.duck';
import {
  ACCOUNT_ROLE_BOTH,
  ACCOUNT_ROLE_KEY,
  ACCOUNT_ROLE_LISTEN,
  ACCOUNT_ROLE_TALK,
  NEEDS_ACCOUNT_ROLE_KEY,
} from '../util/accountRole';
import { isListenerUserType } from '../util/listenerUserType';

const bookingFormatFromPayload = payload => {
  const protectedData = payload?.attributes?.protectedData;
  const format = protectedData?.sessionFormat || protectedData?.bookingDisplayType;
  return typeof format === 'string' && format.length < 40 ? format : null;
};

const signupDataScopes = payload => [
  payload?.publicData,
  payload?.privateData,
  payload?.protectedData,
];

const userTypeFromSignup = payload => {
  const found = signupDataScopes(payload).find(
    data => typeof data?.userType === 'string' && data.userType.trim()
  );
  return found ? found.userType.trim() : null;
};

const accountRoleFromSignup = payload => {
  const found = signupDataScopes(payload).find(data => {
    const role = data?.[ACCOUNT_ROLE_KEY];
    return role === ACCOUNT_ROLE_TALK || role === ACCOUNT_ROLE_LISTEN || role === ACCOUNT_ROLE_BOTH;
  });
  return found ? found[ACCOUNT_ROLE_KEY] : null;
};

/**
 * Talker (buyer) vs listener at signup. Listen role / listener userType => false.
 * Talk/Both role, a non-listener userType, or pending needsAccountRole => true.
 */
export const isTalkerSignupPayload = payload => {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  const userType = userTypeFromSignup(payload);
  const accountRole = accountRoleFromSignup(payload);
  if (accountRole === ACCOUNT_ROLE_LISTEN || isListenerUserType(userType)) {
    return false;
  }
  if (accountRole === ACCOUNT_ROLE_TALK || accountRole === ACCOUNT_ROLE_BOTH) {
    return true;
  }
  if (userType) {
    return true;
  }
  return signupDataScopes(payload).some(data => data?.[NEEDS_ACCOUNT_ROLE_KEY] === true);
};

/** Non-PII signup params. */
export const signupCompletedParams = (payload, method) => ({
  method,
  signup_role: isTalkerSignupPayload(payload) ? 'talker' : 'other',
});

const minorUnits = amount => {
  if (typeof amount === 'number' && Number.isFinite(amount)) {
    return amount;
  }
  if (typeof amount === 'string' && /^\d+$/.test(amount)) {
    return Number(amount);
  }
  return null;
};

const TOPIC_SLUG = /^[a-z0-9][a-z0-9/_-]{0,119}$/i;
const topicSlug = value => {
  if (typeof value !== 'string') return null;
  const slug = value.trim().replace(/^\/+|\/+$/g, '');
  return slug && TOPIC_SLUG.test(slug) ? slug : null;
};
const formatOf = value => (typeof value === 'string' && value && value.length < 40 ? value : null);
const uuidOf = id => {
  const uuid = typeof id === 'string' ? id : id?.uuid;
  return typeof uuid === 'string' && /^[0-9a-f-]{8,64}$/i.test(uuid) ? uuid : null;
};

/** Session context shared by booking_completed and checkout_started (no free text). */
const sessionContextParams = ({ sessionFormat, protectedData, listingId }) => {
  const params = {};
  const format = formatOf(sessionFormat);
  if (format) params.session_format = format;
  params.booking_mode = protectedData?.startNow === true ? 'start_now' : 'book_later';
  const topic = topicSlug(protectedData?.topicPath);
  if (topic) params.topic = topic;
  const listing = uuidOf(listingId);
  if (listing) params.content_ids = [listing];
  return params;
};

/**
 * booking_completed params (paid and free). value/currency USD always;
 * value > 0 only for a positive USD payin. paid=false for free / $0 orders.
 */
export const bookingCompletedParams = (payload, fallbackListingId) => {
  const protectedData = payload?.attributes?.protectedData || {};
  const listingId =
    payload?.listing?.id || payload?.relationships?.listing?.data?.id || fallbackListingId;
  const params = sessionContextParams({
    sessionFormat: bookingFormatFromPayload(payload),
    protectedData,
    listingId,
  });
  const payin = payload?.attributes?.payinTotal;
  const currency = typeof payin?.currency === 'string' ? payin.currency.trim().toUpperCase() : '';
  const minor = minorUnits(payin?.amount);
  const free =
    protectedData.firstSessionFree === true ||
    protectedData.zeroPriceSession === true ||
    minor === 0;
  if (!free && currency === 'USD' && minor != null && minor > 0) {
    params.value = Number((minor / 100).toFixed(2));
    params.paid = true;
  } else {
    params.value = 0;
    params.paid = false;
  }
  params.currency = 'USD';
  return params;
};

/** checkout_started params from the speculate call's orderParams. */
export const checkoutStartedParams = (orderParams, fallbackListingId) => {
  const protectedData = orderParams?.protectedData || {};
  return sessionContextParams({
    sessionFormat: orderParams?.sessionFormat || protectedData.sessionFormat,
    protectedData,
    listingId: orderParams?.listingId || fallbackListingId,
  });
};

const CHECKOUT_DEDUPE_MS = 30 * 60 * 1000;
let lastCheckoutKey = null;
let lastCheckoutAt = 0;
/** True once per listing+format+mode per 30 min (remounts / tax re-price do not repeat). */
export const shouldEmitCheckoutStarted = (params, now = Date.now()) => {
  const key = [params?.content_ids?.[0] || '', params?.session_format || '', params?.booking_mode || ''].join('|');
  if (key === lastCheckoutKey && now - lastCheckoutAt < CHECKOUT_DEDUPE_MS) return false;
  lastCheckoutKey = key;
  lastCheckoutAt = now;
  return true;
};
export const resetCheckoutStartedDedupe = () => {
  lastCheckoutKey = null;
  lastCheckoutAt = 0;
};

const emit = (handlers, eventName, params) => {
  handlers.forEach(handler => {
    if (typeof handler.trackEvent === 'function') {
      handler.trackEvent(eventName, params);
    }
  });
};

// Create a Redux Toolkit listener middleware for analytics handlers. Each
// handler should have the following methods:
//
// - trackPageView(canonicalPath, previousPath): called when the URL is changed
// - trackEvent(eventName, params): optional named events (no PII)
export const createAnalyticsListenerMiddleware = handlers => {
  const listenerMiddleware = createListenerMiddleware();

  listenerMiddleware.startListening({
    actionCreator: locationChanged,
    effect: (action, listenerApi) => {
      const { canonicalPath } = action.payload;
      const previousPath = listenerApi.getOriginalState()?.routing?.currentCanonicalPath;

      handlers.forEach(handler => {
        handler.trackPageView(canonicalPath, previousPath);
      });
    },
  });

  listenerMiddleware.startListening({
    type: 'auth/signup/fulfilled',
    effect: action => {
      emit(handlers, 'signup_completed', signupCompletedParams(action.payload, 'email'));
    },
  });

  listenerMiddleware.startListening({
    type: 'auth/signupWithIdp/fulfilled',
    effect: action => {
      emit(handlers, 'signup_completed', signupCompletedParams(action.payload, 'sso'));
    },
  });

  listenerMiddleware.startListening({
    type: 'CheckoutPage/confirmPayment/fulfilled',
    effect: (action, listenerApi) => {
      const fallbackListingId = listenerApi.getState()?.CheckoutPage?.listing?.id;
      emit(handlers, 'booking_completed', bookingCompletedParams(action.payload, fallbackListingId));
    },
  });

  listenerMiddleware.startListening({
    type: 'CheckoutPage/speculateTransaction/fulfilled',
    effect: (action, listenerApi) => {
      try {
        const fallbackListingId = listenerApi.getState()?.CheckoutPage?.listing?.id;
        const params = checkoutStartedParams(action.meta?.arg?.orderParams, fallbackListingId);
        if (shouldEmitCheckoutStarted(params)) {
          emit(handlers, 'checkout_started', params);
        }
      } catch (e) {
        // never block checkout on analytics
      }
    },
  });

  return listenerMiddleware;
};

// Legacy export for backward compatibility
export const createMiddleware = createAnalyticsListenerMiddleware;
